import React, { useCallback, useContext } from "react";
import { Box } from "@mui/material";
import { Feature } from "geojson";

import { MapContext } from "fond/map/MapProvider";

import { RoadSideClassification } from "./modes/roadSelection";
import PopoutHeader from "./PopoutHeader";
import RoadSelectedPaths, { SideValues } from "./RoadSelectedPaths";
import SelectedPaths from "./SelectedPaths";
import { syncPointCostFactors } from "./syncPoints";

export interface PathPreferencingContentProps {
  features: Feature[];
  returnMode?: string;
  roadSideData?: RoadSideClassification | null;
}

const PathPreferencingContent: React.FC<PathPreferencingContentProps> = ({ features, returnMode, roadSideData }) => {
  const { map, drawControl } = useContext(MapContext);

  /**
   * When the user cancels or applies changes, return to the active tool.
   */
  const returnToActiveTool = useCallback(() => {
    const mode = returnMode || "simple_select";
    drawControl.current?.changeMode(mode);
    map?.fire("draw.modechange", { type: "draw.modechange", mode, userInitiated: true });
    map?.fire("draw.selectionchange", { features: [] });
  }, [drawControl, map, returnMode]);

  const handleCancel = useCallback(() => {
    returnToActiveTool();
  }, [returnToActiveTool]);

  const handleApply = useCallback(
    (value: string, includeCrossings: boolean) => {
      if (!drawControl.current) return;

      const costFactor = parseFloat(value);
      const updatedFeatures: Feature[] = [];
      for (const feature of features) {
        if (includeCrossings || feature.properties?.Crossing !== "YES") {
          const id = feature.id as string;
          if (id != null) {
            drawControl.current.setFeatureProperty(String(id), "CostFactor", costFactor);
            const updated = drawControl.current.get(String(id));
            if (updated) {
              drawControl.current.add(updated);
              updatedFeatures.push(updated);
            }
          }
        }
      }

      const updatedPoints = syncPointCostFactors(drawControl.current, updatedFeatures);
      updatedFeatures.push(...updatedPoints);

      map?.fire("draw.update", { features: updatedFeatures, action: "change_coordinates" });
      returnToActiveTool();
    },
    [drawControl, map, features, returnToActiveTool]
  );

  const handleApplyBySide = useCallback(
    (sideValues: SideValues) => {
      if (!drawControl.current || !roadSideData) return;

      const updatedFeatures: Feature[] = [];

      const applyToIds = (ids: Set<string>, value: string | null) => {
        if (value === null) return;
        const costFactor = parseFloat(value);
        for (const id of ids) {
          drawControl.current!.setFeatureProperty(id, "CostFactor", costFactor);
          const updated = drawControl.current!.get(id);
          if (updated) {
            drawControl.current!.add(updated);
            updatedFeatures.push(updated);
          }
        }
      };

      applyToIds(roadSideData.sideAIds, sideValues.sideA);
      applyToIds(roadSideData.sideBIds, sideValues.sideB);

      // Apply the chosen side's preference to crossings
      if (sideValues.crossingSide) {
        const crossingValue = sideValues.crossingSide === "sideA" ? sideValues.sideA : sideValues.sideB;
        applyToIds(roadSideData.crossingIds, crossingValue);
      }

      const updatedPoints = syncPointCostFactors(drawControl.current, updatedFeatures);
      updatedFeatures.push(...updatedPoints);

      map?.fire("draw.update", { features: updatedFeatures, action: "change_coordinates" });
      returnToActiveTool();
    },
    [drawControl, map, roadSideData, returnToActiveTool]
  );

  const showDualSide = roadSideData && roadSideData.sideAIds.size > 0 && roadSideData.sideBIds.size > 0;

  return (
    <Box p={1.5} display="flex" flexDirection="column" gap={2}>
      <PopoutHeader onClose={handleCancel} />
      {showDualSide ? (
        <RoadSelectedPaths features={features} roadSideData={roadSideData} onCancel={handleCancel} onApply={handleApplyBySide} />
      ) : (
        <SelectedPaths features={features} onCancel={handleCancel} onApply={handleApply} />
      )}
    </Box>
  );
};

export default PathPreferencingContent;
